import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/usePermissions";
import { SystemQuery } from "@/Features/System";
import { getLicenseInfo } from "@/api/licensing";
import { License } from "@/types";
import {
  evaluateStorageLimit,
  formatStorageLimitMessage,
  resolveStorageLimitBytes,
  StorageLimitItem,
  sumSizes,
} from "../utils";
import { useFileUploader } from "./useFileUploader";

export type CheckStorageLimitResult =
  | { allowed: true }
  | { allowed: false; message: string };

/**
 * Client-side courtesy check run before an upload batch is queued. The server
 * (monolith-api, ENG-1795) is the hard gate; this check exists so an
 * over-limit batch never reaches the multipart routes in the normal case.
 *
 * The limit is read fresh from GET /subscription on every check rather than
 * from the login-time `licenseInfo`, which is never refreshed: a limit raised
 * mid-session must apply to the next upload without a re-login, the same way
 * the server reads the license per request.
 *
 * Fails open: if the tenant is not a cloud tenant no request is made, and if
 * the usage or license read fails the batch is allowed and a warning is
 * logged.
 *
 * Callers must hand an allowed batch to `addFiles` synchronously after the
 * check resolves; the batch is counted as pending until then.
 */
export const useStorageLimit = () => {
  const queryClient = useQueryClient();
  const { licenseInfo } = usePermissions();
  const { getPendingBytes, reservePendingBytes } = useFileUploader();

  // is_cloud is a deployment property, not a license setting that changes
  // mid-session, so the login-time value is safe for the short-circuit.
  const isCloud = licenseInfo?.is_cloud === 1;

  const checkStorageLimit = useCallback(
    async (incoming: StorageLimitItem[]): Promise<CheckStorageLimitResult> => {
      // Nothing to upload, nothing to check (an empty folder from the picker)
      if (incoming.length === 0) {
        return { allowed: true };
      }

      // Storage limits only apply to cloud tenants (the server resolves the
      // limit to unlimited off cloud too). On-prem licenses report a numeric
      // 10 PB cap, so without this every batch would pay a /system/info round
      // trip that can never reject.
      if (!isCloud) {
        return { allowed: true };
      }

      let usedBytes: number;
      let limitGb: License["hot_storage_limit_gb"] | undefined;
      try {
        // Usage read is shared with the Settings → System meter's query cache;
        // the license read is not cached anywhere, which is the point.
        const [info, license]: [
          Awaited<ReturnType<typeof SystemQuery.fetchSystemInfo>>,
          Partial<License> | undefined
        ] = await Promise.all([
          SystemQuery.fetchSystemInfo(queryClient, { skipErrorToast: true }),
          getLicenseInfo({ skipErrorToast: true }),
        ]);
        // httpGet resolves { success: false } on failure; a license without
        // the key did not load, and must not read as unlimited.
        if (!license || !("hot_storage_limit_gb" in license)) {
          throw new Error("Tenant license unavailable");
        }
        // Prefer the server gate's own usage number once the API exposes it
        usedBytes = info.files.licensed_size ?? info.files.total_size;
        limitGb = license.hot_storage_limit_gb;
      } catch (error) {
        console.warn(
          "Storage limit check skipped: could not read tenant storage usage.",
          error
        );
        return { allowed: true };
      }

      if (resolveStorageLimitBytes(limitGb) === null) {
        return { allowed: true };
      }

      // Read after the await, through live refs: the queue can change while
      // the request is in flight, and this callback may have been captured
      // earlier (a file picker's onchange) than the batch it is checking.
      const pendingBytes = getPendingBytes();
      const incomingBytes = sumSizes(incoming);

      const result = evaluateStorageLimit({
        limitGb,
        usedBytes,
        pendingBytes,
        incomingBytes,
      });

      if (result.allowed) {
        // Two checks awaiting the same /system/info response would otherwise
        // both pass against the same usage; hold this batch's bytes until the
        // caller queues it with addFiles.
        reservePendingBytes(incomingBytes);
        return { allowed: true };
      }

      return {
        allowed: false,
        message: formatStorageLimitMessage({
          used: usedBytes,
          limit: result.limitBytes,
          incoming: incomingBytes,
          fileCount: incoming.length,
        }),
      };
    },
    [isCloud, queryClient, getPendingBytes, reservePendingBytes]
  );

  return { checkStorageLimit };
};
